/**
 * The monitoring list table: one row per registered connection, sortable by
 * column, with the organization cell doubling as the accessible drilldown
 * control. A connection whose registry state is not active shows that state in
 * place of its runtime status.
 */
import { Link, Stack, Typography } from '@mui/material';
import type { MUIDataTableColumn, MUISortOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';

import type {
  EhrSyncConnection,
  EhrSyncResource,
  EhrSyncStatus,
} from '@models/ehr-sync';

import { formatDate } from '@utils/format';
import { sortDates } from '@utils/sort/date';

import EhrSyncStatusChip from '../../components/EhrSyncStatusChip';
import RegistryStateChip from '../../components/RegistryStateChip';
import { commonMessages, ehrEnvironmentMessages } from '../../messages';
import RelativeTime from '../shared/RelativeTime';
import {
  effectiveStatus,
  organizationSubline,
} from '../shared/monitoring.util';
import ResourcesEnabledCell from './ResourcesEnabledCell';

const messages = defineMessages({
  organization: {
    id: 'ehrSync.list.column.organization',
    defaultMessage: 'Organization',
  },
  network: { id: 'ehrSync.list.column.network', defaultMessage: 'Network' },
  vendor: { id: 'ehrSync.list.column.vendor', defaultMessage: 'EHR Vendor' },
  status: { id: 'ehrSync.list.column.status', defaultMessage: 'Status' },
  lastSync: {
    id: 'ehrSync.list.column.lastSuccessfulSync',
    defaultMessage: 'Last successful sync',
  },
  lastMessage: {
    id: 'ehrSync.list.lastMessage',
    defaultMessage: 'Last message {when}',
  },
  noMessages: {
    id: 'ehrSync.list.noMessages',
    defaultMessage: 'No messages',
  },
  resources: {
    id: 'ehrSync.list.column.resources',
    defaultMessage: 'Resources Enabled',
  },
  goLive: { id: 'ehrSync.list.column.goLive', defaultMessage: 'Go-Live' },
  viewIntegration: {
    id: 'ehrSync.list.viewIntegration',
    defaultMessage: 'View {organization} integration',
  },
  noMatch: {
    id: 'ehrSync.list.noMatch',
    defaultMessage: 'No integrations match these filters.',
  },
});

/** Props for {@link ConnectionsTable}. */
interface ConnectionsTableProps {
  /** Connections to list, one row each. */
  data: EhrSyncConnection[];
  /** Opens a connection's detail view. Without it the rows are not links. */
  onRowClick?: (id: string) => void;
}

/** Row shape handed to the table. Keys double as column names. */
type Row = Pick<
  EhrSyncConnection,
  | 'id'
  | 'organizationName'
  | 'environmentLabel'
  | 'description'
  | 'network'
  | 'ehrVendor'
  | 'status'
  | 'lastSyncAt'
  | 'resources'
  | 'goLiveLabel'
  | 'qieZoneName'
  | 'ehrEnvironment'
  | 'registryState'
  | 'lastReceivedAt'
>;

/** Column order. Cell renderers read sibling values from `rowData` by this index. */
const COLUMN_ORDER: (keyof Row)[] = [
  'id',
  'organizationName',
  'environmentLabel',
  'description',
  'network',
  'ehrVendor',
  'status',
  'lastSyncAt',
  'resources',
  'goLiveLabel',
  'qieZoneName',
  'ehrEnvironment',
  'registryState',
  'lastReceivedAt',
];
const col = (name: keyof Row) => COLUMN_ORDER.indexOf(name);

/** Worst first when sorting ascending, so failures surface at the top. */
const STATUS_ORDER: Record<EhrSyncStatus, number> = {
  failed: 0,
  degraded: 1,
  pending: 2,
  connected: 3,
};

const sortStatus =
  (order: MUISortOptions['direction']) =>
  (
    { data: a }: { data: EhrSyncStatus },
    { data: b }: { data: EhrSyncStatus }
  ) =>
    (STATUS_ORDER[a] - STATUS_ORDER[b]) * (order === 'desc' ? -1 : 1);

/**
 * Sortable table of EHR connections. The organization cell carries a caption
 * with the environment, and the description when it differs from the name.
 *
 * @param props - see {@link ConnectionsTableProps}
 */
export default function ConnectionsTable({
  data,
  onRowClick,
}: ConnectionsTableProps) {
  const { formatMessage } = useIntl();

  const rows: Row[] = data.map((connection) => ({
    id: connection.id,
    organizationName: connection.organizationName,
    environmentLabel: connection.environmentLabel,
    description: connection.description,
    network: connection.network,
    ehrVendor: connection.ehrVendor,
    // Sorts with the registry state folded in, so a missing zone sorts with the failures.
    status: effectiveStatus(connection),
    lastSyncAt: connection.lastSyncAt,
    resources: connection.resources,
    goLiveLabel: connection.goLiveLabel,
    qieZoneName: connection.qieZoneName,
    ehrEnvironment: connection.ehrEnvironment,
    registryState: connection.registryState,
    lastReceivedAt: connection.lastReceivedAt,
  }));

  const columns: MUIDataTableColumn[] = [
    { name: 'id', options: { display: 'excluded' } },
    {
      name: 'organizationName',
      label: formatMessage(messages.organization),
      options: {
        sort: true,
        customBodyRender: (name: Row['organizationName'], { rowData }) => {
          const id = rowData[col('id')] as Row['id'];
          const ehrEnvironment = rowData[
            col('ehrEnvironment')
          ] as Row['ehrEnvironment'];
          const zoneName = rowData[col('qieZoneName')] as Row['qieZoneName'];
          const subline = organizationSubline(name, [
            ehrEnvironment
              ? formatMessage(ehrEnvironmentMessages[ehrEnvironment])
              : (rowData[col('environmentLabel')] as Row['environmentLabel']),
            rowData[col('ehrVendor')] as Row['ehrVendor'],
            rowData[col('description')] as Row['description'],
          ]);
          return (
            <Stack alignItems="flex-start">
              {onRowClick ? (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  fontWeight="bold"
                  underline="hover"
                  textAlign="left"
                  aria-label={formatMessage(messages.viewIntegration, {
                    organization: name,
                  })}
                  onClick={(event: React.MouseEvent) => {
                    // The row itself also navigates; do not fire twice.
                    event.stopPropagation();
                    onRowClick(id);
                  }}
                >
                  {name}
                </Link>
              ) : (
                <Typography variant="body2" fontWeight="bold">
                  {name}
                </Typography>
              )}
              {subline ? (
                <Typography variant="caption" color="text.secondary">
                  {subline}
                </Typography>
              ) : null}
              {zoneName ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {zoneName}
                </Typography>
              ) : null}
            </Stack>
          );
        },
      },
    },
    { name: 'environmentLabel', options: { display: 'excluded' } },
    { name: 'description', options: { display: 'excluded' } },
    {
      name: 'network',
      label: formatMessage(messages.network),
      options: { sort: true },
    },
    {
      name: 'ehrVendor',
      label: formatMessage(messages.vendor),
      options: { sort: true },
    },
    {
      name: 'status',
      label: formatMessage(messages.status),
      options: {
        sort: true,
        sortCompare: sortStatus,
        customBodyRender: (status: Row['status'], { rowData }) => {
          const registryState = rowData[
            col('registryState')
          ] as Row['registryState'];
          return registryState && registryState !== 'active' ? (
            <RegistryStateChip state={registryState} />
          ) : (
            <EhrSyncStatusChip status={status} />
          );
        },
      },
    },
    {
      name: 'lastSyncAt',
      label: formatMessage(messages.lastSync),
      options: {
        sort: true,
        sortCompare: sortDates,
        customBodyRender: (iso: Row['lastSyncAt'], { rowData }) => {
          const received = rowData[
            col('lastReceivedAt')
          ] as Row['lastReceivedAt'];
          return (
            <Stack>
              <Typography variant="body2" color="text.primary">
                {iso
                  ? formatDate({ date: iso, formatter: 'datetime' })
                  : formatMessage(commonMessages.never)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {received
                  ? formatMessage(messages.lastMessage, {
                      when: <RelativeTime iso={received} />,
                    })
                  : formatMessage(messages.noMessages)}
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: 'resources',
      label: formatMessage(messages.resources),
      options: {
        customBodyRender: (resources: EhrSyncResource[]) => (
          <ResourcesEnabledCell resources={resources} />
        ),
      },
    },
    {
      name: 'goLiveLabel',
      label: formatMessage(messages.goLive),
      options: { sort: true },
    },
    { name: 'qieZoneName', options: { display: 'excluded' } },
    { name: 'ehrEnvironment', options: { display: 'excluded' } },
    { name: 'registryState', options: { display: 'excluded' } },
    { name: 'lastReceivedAt', options: { display: 'excluded' } },
  ];

  return (
    <MuiDataTable
      columns={columns}
      data={rows}
      options={{
        sort: true,
        sortOrder: { name: 'organizationName', direction: 'asc' },
        elevation: 0,
        textLabels: { body: { noMatch: formatMessage(messages.noMatch) } },
        onRowClick: onRowClick
          ? (_rowData, { dataIndex }) => onRowClick(data[dataIndex].id)
          : undefined,
        setRowProps: () => ({
          style: { cursor: onRowClick ? 'pointer' : 'default' },
        }),
      }}
    />
  );
}
